import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Package, Store, Wallet, Truck, CheckCircle2, Clock, Trash2, AlertTriangle, Upload, X, Loader2, Star, Copy, Link2, Plus, Sparkles, Crown, ExternalLink, Share2, MessageCircle, Twitter, Facebook, TrendingUp, Zap, Repeat, Bell, Download, Heart } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { api, imgUrl, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import StarRating from "@/components/StarRating";
import { toast } from "sonner";

const SHIPPING_BADGE_URL = "https://static.prod-images.emergentagent.com/jobs/a3afa2e1-4161-4367-92df-240b41268e55/images/49e1877a529553345af2d01836e9c52a40b2710a9adb6135c26db7b0012799de.jpeg";
const THANK_YOU_CARD_URL = "https://static.prod-images.emergentagent.com/jobs/a3afa2e1-4161-4367-92df-240b41268e55/images/9183416931fa0cce75531da4add9aa12b9eaec30e21ace870058222b012d995b.jpeg";

const STATUS_META = {
  paid: { label: "Paid — awaiting shipment", color: "text-blue-600", icon: Clock },
  shipped: { label: "Shipped", color: "text-amber-600", icon: Truck },
  completed: { label: "Completed", color: "text-green-600", icon: CheckCircle2 },
  disputed: { label: "Disputed — under review", color: "text-red-600", icon: AlertTriangle },
  refunded: { label: "Refunded", color: "text-neutral-500", icon: Clock },
  cancelled: { label: "Cancelled", color: "text-red-600", icon: Clock },
};

function StatusPill({ status }) {
  const m = STATUS_META[status] || { label: status, color: "text-neutral-600", icon: Clock };
  const Icon = m.icon;
  return <span className={`inline-flex items-center gap-1 text-xs font-semibold ${m.color}`}><Icon size={13} /> {m.label}</span>;
}

export default function Dashboard() {
  const { user, refreshUser, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const [purchases, setPurchases] = useState([]);
  const [sales, setSales] = useState([]);
  const [listings, setListings] = useState([]);
  const [shipOrder, setShipOrder] = useState(null);
  const [carrier, setCarrier] = useState("");
  const [tracking, setTracking] = useState("");
  const [issueOrder, setIssueOrder] = useState(null);
  const [issueReason, setIssueReason] = useState("");
  const [issueImages, setIssueImages] = useState([]);
  const [issueUploading, setIssueUploading] = useState(false);
  const [myPicks, setMyPicks] = useState([]);
  const [rateOrder, setRateOrder] = useState(null);
  const [rateStars, setRateStars] = useState(5);
  const [rateComment, setRateComment] = useState("");
  const [allProducts, setAllProducts] = useState([]);
  const [pickProductId, setPickProductId] = useState("");
  const [pickNote, setPickNote] = useState("");
  const [connectStatus, setConnectStatus] = useState(null);
  const [applying, setApplying] = useState(false);
  const [estSales, setEstSales] = useState(500);
  const [estVips, setEstVips] = useState(10);
  const [boostCredits, setBoostCredits] = useState(0);
  const [buyingBoosts, setBuyingBoosts] = useState(false);
  const [swaps, setSwaps] = useState([]);
  const [swapShip, setSwapShip] = useState(null);
  const [swapCarrier, setSwapCarrier] = useState("");
  const [swapTracking, setSwapTracking] = useState("");
  const [swapBusy, setSwapBusy] = useState(false);
  const [rateSwap, setRateSwap] = useState(null);
  const [rateSwapStars, setRateSwapStars] = useState(5);
  const [rateSwapComment, setRateSwapComment] = useState("");
  const [chatSwapId, setChatSwapId] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatText, setChatText] = useState("");
  const [chatBusy, setChatBusy] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const isInfluencer = user?.influencer_status === "approved" || user?.role === "admin";

  const load = useCallback(async () => {
    const [p, s, l] = await Promise.all([
      api.get("/orders/purchases").then((r) => r.data).catch(() => []),
      api.get("/orders/sales").then((r) => r.data).catch(() => []),
      api.get("/my/products").then((r) => r.data).catch(() => []),
    ]);
    setPurchases(p); setSales(s); setListings(l);
    api.get("/swaps/mine").then((r) => setSwaps(r.data || [])).catch(() => {});
    if (user?.seller_status === "approved" || user?.role === "admin") {
      api.get("/seller/credits").then((r) => setBoostCredits(r.data.ai_credits)).catch(() => {});
    }
    if (user?.influencer_status === "approved" || user?.role === "admin") {
      const [picks, prods] = await Promise.all([
        api.get("/my/picks").then((r) => r.data).catch(() => []),
        api.get("/products").then((r) => r.data).catch(() => []),
      ]);
      setMyPicks(picks); setAllProducts(prods);
      api.get("/seller/connect/status").then((r) => setConnectStatus(r.data)).catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/"); return; }
    load();
  }, [user, loading, load, navigate]);

  const submitShip = async () => {
    if (!carrier.trim()) { toast.error("Please enter how you shipped it"); return; }
    try {
      await api.post(`/orders/${shipOrder.id}/ship`, { carrier, tracking });
      toast.success("Buyer notified that the item has shipped");
      setShipOrder(null); setCarrier(""); setTracking(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const confirm = async (id) => {
    try { await api.post(`/orders/${id}/confirm`); toast.success("Receipt confirmed. Seller has been paid."); load(); refreshUser(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const uploadIssueImg = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setIssueUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setIssueImages((prev) => [...prev, data.path]);
      }
    } catch (err) { toast.error(apiError(err)); }
    finally { setIssueUploading(false); }
  };
  const submitIssue = async () => {
    if (!issueReason.trim()) { toast.error("Please explain what went wrong"); return; }
    try {
      await api.post(`/orders/${issueOrder.id}/report-issue`, { reason: issueReason, images: issueImages });
      toast.success("Problem reported — our team has been alerted.");
      setIssueOrder(null); setIssueReason(""); setIssueImages([]); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const submitSellerReview = async () => {
    try {
      await api.post(`/sellers/${rateOrder.seller_id}/reviews`, { rating: rateStars, comment: rateComment });
      toast.success("Thanks for rating your seller!");
      setRateOrder(null);
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => {
    try { await api.delete(`/products/${id}`); toast.success("Listing removed"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  // Finalize a swap-fee payment when returning from Stripe
  useEffect(() => {
    const ss = searchParams.get("swap_session");
    if (!ss) return;
    api.get(`/swaps/pay/status/${ss}`).then(({ data }) => {
      if (data.payment_status === "paid") toast.success("Swap fee paid! ✅");
      load();
    }).catch(() => {}).finally(() => {
      searchParams.delete("swap_session"); setSearchParams(searchParams, { replace: true });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const respondSwap = async (id, accept) => {
    setSwapBusy(true);
    try {
      const { data } = await api.post(`/swaps/${id}/respond`, { accept });
      toast.success(data.message); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSwapBusy(false); }
  };
  const paySwap = async (id) => {
    setSwapBusy(true);
    try {
      const { data } = await api.post(`/swaps/${id}/pay`, { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(apiError(e)); setSwapBusy(false); }
  };
  const submitSwapShip = async () => {
    if (!swapCarrier.trim()) { toast.error("Please enter how you shipped it"); return; }
    setSwapBusy(true);
    try {
      await api.post(`/swaps/${swapShip.id}/ship`, { carrier: swapCarrier, tracking: swapTracking });
      toast.success("Marked as shipped");
      setSwapShip(null); setSwapCarrier(""); setSwapTracking(""); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSwapBusy(false); }
  };
  const confirmSwap = async (id) => {
    setSwapBusy(true);
    try { const { data } = await api.post(`/swaps/${id}/confirm`); toast.success(data.message); load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setSwapBusy(false); }
  };
  const cancelSwap = async (id) => {
    if (!window.confirm("Cancel this swap? Any paid swap fees will be refunded.")) return;
    setSwapBusy(true);
    try { const { data } = await api.post(`/swaps/${id}/cancel`); toast.success(data.message); load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setSwapBusy(false); }
  };
  const submitSwapReview = async () => {
    setSwapBusy(true);
    try {
      const { data } = await api.post(`/swaps/${rateSwap.id}/review`, { rating: rateSwapStars, comment: rateSwapComment });
      toast.success(data.message);
      setRateSwap(null); setRateSwapComment(""); setRateSwapStars(5); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSwapBusy(false); }
  };

  const fetchChat = (id) => api.get(`/swaps/${id}/messages`).then(({ data }) => setChatMessages(data || [])).catch(() => {});
  const toggleChat = (id) => {
    if (chatSwapId === id) { setChatSwapId(null); setChatMessages([]); return; }
    setChatSwapId(id); setChatMessages([]); setChatText(""); fetchChat(id);
    setSwaps((list) => list.map((s) => s.id === id ? { ...s, unread: 0 } : s));
  };
  useEffect(() => {
    if (!chatSwapId) return;
    const t = setInterval(() => fetchChat(chatSwapId), 5000);
    return () => clearInterval(t);
  }, [chatSwapId]);
  const sendChat = async () => {
    if (!chatText.trim()) return;
    setChatBusy(true);
    try {
      const { data } = await api.post(`/swaps/${chatSwapId}/messages`, { text: chatText });
      setChatMessages((m) => [...m, data]); setChatText("");
    } catch (e) { toast.error(apiError(e)); }
    finally { setChatBusy(false); }
  };

  const toggleTradeAlerts = async () => {
    try {
      const { data } = await api.patch("/me/preferences", { trade_alerts: !user.trade_alerts });
      setUser(data);
      toast.success(data.trade_alerts ? "Trade offer alerts on — we'll email you about new offers 🔔" : "Trade offer alerts off — you won't get offer emails");
    } catch (e) { toast.error(apiError(e)); }
  };

  const applyInfluencer = async () => {
    setApplying(true);
    try {
      await api.post("/influencer/apply");
      toast.success("Application submitted! An admin will review it shortly.");
      refreshUser();
    } catch (e) { toast.error(apiError(e)); }
    finally { setApplying(false); }
  };
  const createPick = async () => {
    if (!pickProductId) { toast.error("Select a product to feature"); return; }
    try {
      await api.post("/picks", { product_id: pickProductId, note: pickNote });
      toast.success("Pick added!");
      setPickProductId(""); setPickNote(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const delPick = async (id) => {
    try { await api.delete(`/picks/${id}`); toast.success("Pick removed"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const connectPayout = async () => {
    try {
      const { data } = await api.post("/seller/connect/onboard", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(apiError(e)); }
  };
  const buyBoosts = async () => {
    setBuyingBoosts(true);
    try {
      const { data } = await api.post("/seller/credits/checkout", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(apiError(e)); setBuyingBoosts(false); }
  };
  const copyPickLink = (pick) => {
    const link = `${window.location.origin}/product/${pick.product.id}${user.influencer_code ? `?ref=${user.influencer_code}` : ""}`;
    navigator.clipboard.writeText(link);
    toast.success("Referral link copied!");
  };
  const membershipLink = `${window.location.origin}/vip${user?.influencer_code ? `?ref=${user.influencer_code}` : ""}`;
  const copyMembershipLink = () => {
    navigator.clipboard.writeText(membershipLink);
    toast.success("Membership link copied!");
  };
  const creatorLink = `${window.location.origin}/creator/${user?.influencer_code || ""}`;
  const copyCreatorLink = () => {
    navigator.clipboard.writeText(creatorLink);
    toast.success("Your page link copied!");
  };
  const shareCreatorPage = async () => {
    const text = `Shop my beauty picks & join VIP on Beauty Trade Club ✨`;
    if (navigator.share) {
      try { await navigator.share({ title: "My Beauty Trade Club page", text, url: creatorLink }); return; }
      catch { /* user cancelled or unsupported */ }
    }
    navigator.clipboard.writeText(`${text} ${creatorLink}`);
    toast.success("Link copied — paste it into your Instagram/TikTok bio!");
  };
  const shareTo = (network) => {
    const text = encodeURIComponent("Shop my beauty picks & join VIP on Beauty Trade Club ✨");
    const url = encodeURIComponent(creatorLink);
    const urls = {
      whatsapp: `https://wa.me/?text=${text}%20${url}`,
      x: `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
    };
    window.open(urls[network], "_blank", "noopener,noreferrer");
  };
  const estEarnings = (estSales * 0.10 + estVips * 1.5);

  if (!user) return null;
  const isSeller = user.seller_status === "approved" || user.role === "admin";

  return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-3xl font-bold">My Dashboard</h1>
          <p className="text-neutral-500">Welcome back, {user.name}</p>
        </div>
        {isSeller && (
          <div className="flex items-center gap-3">
            <div data-testid="seller-balance" className="flex items-center gap-3 bg-white border border-[#E8956B]/40 rounded-2xl px-5 py-3">
              <Wallet className="text-[#E8956B]" />
              <div>
                <div className="text-xs text-neutral-500 uppercase tracking-wide">Available Balance</div>
                <div className="text-xl font-bold">${(user.balance || 0).toFixed(2)}</div>
              </div>
            </div>
            <div data-testid="boost-balance" className="flex items-center gap-3 bg-white border border-[#E8956B]/40 rounded-2xl px-5 py-3">
              <Zap className="text-[#E8956B]" />
              <div>
                <div className="text-xs text-neutral-500 uppercase tracking-wide">Photo Boosts</div>
                <div className="flex items-baseline gap-2">
                  <span className="text-xl font-bold" data-testid="dashboard-boost-count">{boostCredits}</span>
                  <button type="button" data-testid="dashboard-buy-boosts-btn" onClick={buyBoosts} disabled={buyingBoosts}
                    className="text-xs font-semibold text-[#E8956B] hover:underline disabled:opacity-50">
                    {buyingBoosts ? "..." : "Buy (5 for $2.99)"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <Tabs defaultValue="purchases">
        <TabsList className="bg-neutral-50 border border-neutral-200">
          <TabsTrigger value="purchases" data-testid="tab-purchases"><Package size={15} className="mr-1.5" /> Purchases</TabsTrigger>
          <TabsTrigger value="trades" data-testid="tab-trades" className="relative"><Repeat size={15} className="mr-1.5" /> Trades{swaps.length ? ` (${swaps.length})` : ""}
            {swaps.reduce((n, s) => n + (s.unread || 0), 0) > 0 && (
              <span data-testid="trades-unread-dot" className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-red-500 ring-2 ring-white" />
            )}
          </TabsTrigger>
          {isSeller && <TabsTrigger value="sales" data-testid="tab-sales"><Store size={15} className="mr-1.5" /> Sales</TabsTrigger>}
          {isSeller && <TabsTrigger value="listings" data-testid="tab-listings">My Listings</TabsTrigger>}
          <TabsTrigger value="influencer" data-testid="tab-influencer"><Star size={15} className="mr-1.5" /> Influencer</TabsTrigger>
        </TabsList>

        {/* Purchases */}
        <TabsContent value="purchases" className="mt-6 space-y-4">
          {purchases.length === 0 && <p className="text-neutral-400">No purchases yet. <button onClick={() => navigate("/shop")} className="text-[#E8956B]">Start shopping</button></p>}
          {purchases.map((o) => (
            <div key={o.id} data-testid={`purchase-${o.id}`} className="flex items-center gap-4 bg-white border border-neutral-200 rounded-xl p-4">
              <img src={imgUrl(o.product_image)} alt="" className="w-16 h-16 rounded-lg object-cover" />
              <div className="flex-1">
                <div className="font-semibold">{o.product_title}</div>
                <div className="text-sm text-neutral-500">${o.amount.toFixed(2)} · from {o.seller_name}</div>
                <StatusPill status={o.status} />
                {o.status === "shipped" && o.shipped_carrier && (
                  <div data-testid={`ship-info-${o.id}`} className="text-xs text-neutral-700 font-medium mt-1 flex items-center gap-1">
                    <Truck size={12} className="text-[#E8956B]" /> Shipped via {o.shipped_carrier}{o.shipped_tracking ? ` · Tracking: ${o.shipped_tracking}` : ""}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2 items-end">
                {o.status === "shipped" && (
                  <button data-testid={`confirm-${o.id}`} onClick={() => confirm(o.id)} className="bg-[#E8956B] hover:bg-[#CE744A] text-white text-sm font-bold rounded-full px-4 py-2 transition-all">
                    Confirm Receipt
                  </button>
                )}
                {(o.status === "paid" || o.status === "shipped") && (
                  <button data-testid={`report-${o.id}`} onClick={() => { setIssueOrder(o); setIssueReason(""); setIssueImages([]); }} className="text-xs font-semibold text-red-600 hover:underline">
                    Report a problem
                  </button>
                )}
                {(o.status === "completed" || o.status === "shipped") && (
                  <button data-testid={`rate-seller-${o.id}`} onClick={() => { setRateOrder(o); setRateStars(5); setRateComment(""); }} className="text-xs font-semibold text-[#E8956B] hover:underline flex items-center gap-1">
                    <Star size={12} /> Rate seller
                  </button>
                )}
              </div>
            </div>
          ))}
        </TabsContent>

        {/* Trades / Swaps */}
        <TabsContent value="trades" className="mt-6 space-y-4">
          <div data-testid="trade-alerts-pref" className="flex items-center justify-between rounded-xl border border-neutral-200 bg-white px-4 py-3">
            <div className="flex items-center gap-2 text-sm text-neutral-700">
              <Bell size={16} className="text-[#E8956B]" />
              <span>Email me when someone offers me a swap</span>
            </div>
            <button
              data-testid="toggle-trade-alerts"
              onClick={toggleTradeAlerts}
              role="switch"
              aria-checked={!!user?.trade_alerts}
              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${user?.trade_alerts ? "bg-[#E8956B]" : "bg-neutral-300"}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${user?.trade_alerts ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          </div>
          {swaps.length === 0 && (
            <p className="text-neutral-400" data-testid="no-trades">
              No trades yet. Find an item you love and tap <span className="text-[#E8956B] font-semibold">Propose a Swap</span> to offer a trade.
            </p>
          )}
          {swaps.map((sw) => {
            const role = user.id === sw.owner_id ? "owner" : "proposer";
            const myPaid = sw[`${role}_paid`];
            const otherRole = role === "owner" ? "proposer" : "owner";
            const otherPaid = sw[`${otherRole}_paid`];
            const myShipped = sw[`${role}_shipped`];
            const otherShipped = sw[`${otherRole}_shipped`];
            const myConfirmed = sw[`${role}_confirmed`];
            const bothShipped = sw.proposer_shipped && sw.owner_shipped;
            const otherName = role === "owner" ? sw.proposer_name : sw.owner_name;
            const giving = role === "owner" ? sw.target_title : (sw.offer_title || sw.offer_description);
            const getting = role === "owner" ? (sw.offer_title || sw.offer_description) : sw.target_title;
            const STATUS = {
              proposed: { label: "Offer sent — awaiting response", color: "text-blue-600" },
              accepted: { label: "Accepted — pay your swap fee", color: "text-amber-600" },
              active: { label: "In the process — ship & confirm", color: "text-amber-600" },
              completed: { label: "Trade complete", color: "text-green-600" },
            }[sw.status] || { label: sw.status, color: "text-neutral-600" };
            return (
              <div key={sw.id} data-testid={`swap-${sw.id}`} className="bg-white border border-neutral-200 rounded-xl p-4">
                <div className="flex items-center gap-3 flex-wrap">
                  <img src={imgUrl(sw.target_image)} alt="" className="w-14 h-14 rounded-lg object-cover" />
                  <Repeat size={18} className="text-[#E8956B]" />
                  {sw.offer_image ? <img src={imgUrl(sw.offer_image)} alt="" className="w-14 h-14 rounded-lg object-cover" />
                    : <div className="w-14 h-14 rounded-lg bg-neutral-100 flex items-center justify-center text-[10px] text-neutral-400 text-center px-1">{sw.offer_description?.slice(0, 22) || "Item"}</div>}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold">
                      You give <span className="text-neutral-900">{giving}</span> · get <span className="text-neutral-900">{getting}</span>
                    </div>
                    <div className="text-xs text-neutral-500">with {otherName}</div>
                    <span className={`inline-flex items-center gap-1 text-xs font-semibold mt-1 ${STATUS.color}`}>
                      <Repeat size={12} /> {STATUS.label}
                    </span>
                  </div>
                </div>
                {sw.note && <p className="text-xs text-neutral-500 mt-2 italic">“{sw.note}”</p>}

                <div className="flex flex-wrap gap-2 mt-3 items-center">
                  {sw.status === "proposed" && role === "owner" && (
                    <>
                      <button data-testid={`swap-accept-${sw.id}`} disabled={swapBusy} onClick={() => respondSwap(sw.id, true)}
                        className="bg-[#E8956B] hover:bg-[#CE744A] text-white text-sm font-bold rounded-full px-4 py-2 transition-all disabled:opacity-50">Accept swap</button>
                      <button data-testid={`swap-decline-${sw.id}`} disabled={swapBusy} onClick={() => respondSwap(sw.id, false)}
                        className="border border-neutral-300 text-neutral-600 text-sm font-semibold rounded-full px-4 py-2 hover:bg-neutral-100 transition-all disabled:opacity-50">Decline</button>
                    </>
                  )}
                  {sw.status === "proposed" && role === "proposer" && (
                    <span className="text-xs text-neutral-500">Waiting for {otherName} to accept or decline.</span>
                  )}
                  {sw.status === "accepted" && !myPaid && (
                    <button data-testid={`swap-pay-${sw.id}`} disabled={swapBusy} onClick={() => paySwap(sw.id)}
                      className="bg-[#E8956B] hover:bg-[#CE744A] text-white text-sm font-bold rounded-full px-4 py-2 transition-all disabled:opacity-50">Pay $4.99 swap fee</button>
                  )}
                  {sw.status === "accepted" && myPaid && !otherPaid && (
                    <span className="text-xs text-green-600 font-semibold">You've paid ✓ — waiting for {otherName} to pay.</span>
                  )}
                  {sw.status === "active" && !myShipped && (
                    <button data-testid={`swap-ship-${sw.id}`} disabled={swapBusy} onClick={() => { setSwapShip(sw); setSwapCarrier(""); setSwapTracking(""); }}
                      className="bg-[#E8956B] hover:bg-[#CE744A] text-white text-sm font-bold rounded-full px-4 py-2 transition-all disabled:opacity-50">Mark shipped</button>
                  )}
                  {sw.status === "active" && myShipped && !bothShipped && (
                    <span className="text-xs text-neutral-500">Shipped ✓ — waiting for {otherName} to ship.</span>
                  )}
                  {sw.status === "active" && bothShipped && !myConfirmed && (
                    <button data-testid={`swap-confirm-${sw.id}`} disabled={swapBusy} onClick={() => confirmSwap(sw.id)}
                      className="bg-[#E8956B] hover:bg-[#CE744A] text-white text-sm font-bold rounded-full px-4 py-2 transition-all disabled:opacity-50">Confirm receipt</button>
                  )}
                  {sw.status === "active" && myConfirmed && (
                    <span className="text-xs text-green-600 font-semibold">Receipt confirmed ✓ — waiting for {otherName}.</span>
                  )}
                  {sw.status === "completed" && <span className="text-xs text-green-600 font-semibold">🎉 All done — enjoy your trade!</span>}
                  {sw.status === "completed" && (sw.i_reviewed
                    ? <span data-testid={`swap-rated-${sw.id}`} className="text-xs text-neutral-500 flex items-center gap-1"><Star size={12} className="text-[#E8956B]" fill="currentColor" /> You rated {otherName} ✓</span>
                    : <button data-testid={`swap-rate-${sw.id}`} onClick={() => { setRateSwap({ ...sw, otherName }); setRateSwapStars(5); setRateSwapComment(""); }}
                        className="text-xs font-semibold text-[#E8956B] hover:underline flex items-center gap-1"><Star size={12} /> Rate {otherName}</button>
                  )}
                  {["proposed", "accepted", "active"].includes(sw.status) && !bothShipped && (
                    <button data-testid={`swap-cancel-${sw.id}`} disabled={swapBusy} onClick={() => cancelSwap(sw.id)}
                      className="text-xs font-semibold text-red-600 hover:underline ml-auto">Cancel swap</button>
                  )}
                </div>

                {sw.status !== "proposed" && (
                  <button data-testid={`swap-chat-toggle-${sw.id}`} onClick={() => toggleChat(sw.id)}
                    className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[#CE744A] hover:underline">
                    <MessageCircle size={13} /> {chatSwapId === sw.id ? "Hide chat" : `Message ${otherName}`}
                    {chatSwapId !== sw.id && sw.unread > 0 && (
                      <span data-testid={`swap-unread-${sw.id}`} className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold">{sw.unread}</span>
                    )}
                  </button>
                )}
                {chatSwapId === sw.id && (
                  <div data-testid={`swap-chat-${sw.id}`} className="mt-3 rounded-xl border border-neutral-200 bg-neutral-50 p-3">
                    <div className="max-h-52 overflow-y-auto space-y-2 mb-2">
                      {chatMessages.length === 0 && <p className="text-xs text-neutral-400 text-center py-3">No messages yet. Say hi and sort out shipping details! 👋</p>}
                      {chatMessages.map((m) => {
                        const mine = m.sender_id === user.id;
                        return (
                          <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                            <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${mine ? "bg-[#E8956B] text-white" : "bg-white border border-neutral-200 text-neutral-800"}`}>
                              {!mine && <div className="text-[10px] font-bold opacity-70 mb-0.5">{m.sender_name}</div>}
                              {m.text}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex gap-2">
                      <Input data-testid={`swap-chat-input-${sw.id}`} value={chatText} onChange={(e) => setChatText(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") sendChat(); }}
                        placeholder="Type a message…" className="bg-white border-neutral-300 h-10" />
                      <button data-testid={`swap-chat-send-${sw.id}`} disabled={chatBusy || !chatText.trim()} onClick={sendChat}
                        className="bg-[#E8956B] hover:bg-[#CE744A] text-white text-sm font-bold rounded-full px-4 transition-all disabled:opacity-50">Send</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </TabsContent>

        {/* Sales */}
        {isSeller && (
          <TabsContent value="sales" className="mt-6 space-y-4">
            <div data-testid="shipping-kit" className="rounded-2xl border border-[#E8956B]/30 bg-[#E8956B]/5 p-5">
              <div className="flex items-center gap-2 mb-1">
                <Heart size={18} className="text-[#E8956B]" fill="currentColor" />
                <h3 className="font-display text-lg font-bold text-neutral-900">Your Shipping Kit</h3>
              </div>
              <p className="text-sm text-neutral-500 mb-4">Add a little brand magic to every order — print &amp; pop these on the box you ship. Print the sticker on sticker paper (or tape it on) and tuck the thank-you card inside.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="bg-white rounded-xl border border-neutral-200 p-4 text-center">
                  <img src={SHIPPING_BADGE_URL} alt="Beauty Trade Club packed with love badge" className="w-32 h-32 object-contain mx-auto" />
                  <p className="text-sm font-semibold text-neutral-800 mt-2">"Packed with love" sticker</p>
                  <a href={SHIPPING_BADGE_URL} download="beauty-trade-club-sticker.png" target="_blank" rel="noreferrer"
                    data-testid="download-badge-btn"
                    className="mt-3 inline-flex items-center justify-center gap-1.5 w-full bg-[#E8956B] hover:bg-[#CE744A] text-white text-sm font-bold rounded-full py-2.5 transition-all">
                    <Download size={15} /> Download sticker
                  </a>
                </div>
                <div className="bg-white rounded-xl border border-neutral-200 p-4 text-center">
                  <img src={THANK_YOU_CARD_URL} alt="Beauty Trade Club thank you card" className="w-full h-32 object-contain mx-auto rounded-lg" />
                  <p className="text-sm font-semibold text-neutral-800 mt-2">Thank-you card</p>
                  <a href={THANK_YOU_CARD_URL} download="beauty-trade-club-thank-you-card.png" target="_blank" rel="noreferrer"
                    data-testid="download-card-btn"
                    className="mt-3 inline-flex items-center justify-center gap-1.5 w-full border-2 border-[#E8956B] text-[#CE744A] hover:bg-[#E8956B]/10 text-sm font-bold rounded-full py-2.5 transition-all">
                    <Download size={15} /> Download card
                  </a>
                </div>
              </div>
            </div>
            {sales.length === 0 && <p className="text-neutral-400">No sales yet.</p>}
            {sales.map((o) => (
              <div key={o.id} data-testid={`sale-${o.id}`} className="flex items-center gap-4 bg-white border border-neutral-200 rounded-xl p-4">
                <img src={imgUrl(o.product_image)} alt="" className="w-16 h-16 rounded-lg object-cover" />
                <div className="flex-1">
                  <div className="font-semibold">{o.product_title}</div>
                  <div className="text-sm text-neutral-500">Payout ${o.seller_payout.toFixed(2)} (fee ${o.platform_fee.toFixed(2)}) · to {o.buyer_name}</div>
                  <StatusPill status={o.status} />
                  {o.status === "shipped" && o.shipped_carrier && (
                    <div className="text-xs text-neutral-700 font-medium mt-1 flex items-center gap-1">
                      <Truck size={12} className="text-[#E8956B]" /> Sent via {o.shipped_carrier}{o.shipped_tracking ? ` · ${o.shipped_tracking}` : ""}
                    </div>
                  )}
                </div>
                {o.status === "paid" && (
                  <button data-testid={`ship-${o.id}`} onClick={() => { setShipOrder(o); setCarrier(""); setTracking(""); }} className="bg-[#E8956B] hover:bg-[#CE744A] text-white text-sm font-bold rounded-full px-4 py-2 transition-all">
                    Mark Shipped
                  </button>
                )}
                {o.status === "shipped" && <span className="text-xs text-neutral-500">Awaiting buyer confirmation</span>}
              </div>
            ))}
          </TabsContent>
        )}

        {/* Listings */}
        {isSeller && (
          <TabsContent value="listings" className="mt-6">
            <button onClick={() => navigate("/sell")} className="mb-4 border-2 border-[#E8956B] text-[#E8956B] hover:bg-[#E8956B] hover:text-white font-bold rounded-full px-5 py-2 text-sm transition-all">
              + New Listing
            </button>
            <div className="grid sm:grid-cols-2 gap-4">
              {listings.map((p) => (
                <div key={p.id} data-testid={`listing-${p.id}`} className="flex items-center gap-3 bg-white border border-neutral-200 rounded-xl p-3">
                  <img src={imgUrl(p.images?.[0])} alt="" className="w-14 h-14 rounded-lg object-cover cursor-pointer" onClick={() => navigate(`/product/${p.id}`)} />
                  <div className="flex-1">
                    <div className="font-semibold text-sm">{p.title}</div>
                    <div className="text-xs text-neutral-500">${p.price.toFixed(2)} · {p.status}</div>
                  </div>
                  <button onClick={() => del(p.id)} className="text-neutral-400 hover:text-red-400"><Trash2 size={16} /></button>
                </div>
              ))}
              {listings.length === 0 && <p className="text-neutral-400 text-sm">No listings yet.</p>}
            </div>
          </TabsContent>
        )}

        {/* Influencer */}
        <TabsContent value="influencer" className="mt-6">
          {!isInfluencer ? (
            <div data-testid="influencer-apply-card" className="bg-white border border-neutral-200 rounded-2xl p-8 text-center max-w-xl mx-auto">
              <Star size={32} className="text-[#E8956B] mx-auto mb-3" fill="currentColor" />
              <h3 className="font-display text-2xl font-bold">Become an Influencer</h3>
              <p className="text-neutral-500 mt-2 text-sm">
                Recommend the beauty you love. When someone buys through your pick, you earn a{" "}
                <span className="font-bold text-[#E8956B]">10% commission</span> — sellers keep 85% and the platform only takes 5%.
              </p>
              {user.influencer_status === "pending" ? (
                <div data-testid="influencer-pending" className="mt-5 inline-flex items-center gap-2 text-amber-600 font-semibold text-sm">
                  <Clock size={16} /> Application under review
                </div>
              ) : (
                <button
                  data-testid="apply-influencer-btn"
                  onClick={applyInfluencer}
                  disabled={applying}
                  className="mt-5 bg-[#E8956B] hover:bg-[#CE744A] text-white font-bold rounded-full px-6 py-2.5 transition-all disabled:opacity-50"
                >
                  {user.influencer_status === "rejected" ? "Re-apply" : "Apply Now"}
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-8">
              <div data-testid="influencer-getting-started" className="bg-gradient-to-br from-[#E8956B]/10 to-[#E8956B]/5 border border-[#E8956B]/30 rounded-2xl p-5">
                <h3 className="font-display text-lg font-bold flex items-center gap-2"><Sparkles size={18} className="text-[#E8956B]" /> Getting Started</h3>
                <ol className="mt-3 space-y-2 text-sm text-neutral-700">
                  <li className="flex gap-2"><span className="w-5 h-5 rounded-full bg-[#E8956B] text-white text-xs font-bold flex items-center justify-center shrink-0">1</span> Pick a product you love below and add a short note.</li>
                  <li className="flex gap-2"><span className="w-5 h-5 rounded-full bg-[#E8956B] text-white text-xs font-bold flex items-center justify-center shrink-0">2</span> Share the link (Copy button) — it carries your code <b className="text-[#E8956B]">{user.influencer_code}</b>.</li>
                  <li className="flex gap-2"><span className="w-5 h-5 rounded-full bg-[#E8956B] text-white text-xs font-bold flex items-center justify-center shrink-0">3</span> Set up Payouts so your <b>10% commission</b> can be paid out.</li>
                  <li className="flex gap-2"><span className="w-5 h-5 rounded-full bg-[#E8956B] text-white text-xs font-bold flex items-center justify-center shrink-0">4</span> Your picks show on the public <button onClick={() => navigate("/picks")} className="text-[#E8956B] font-semibold underline">Picks page</button> for everyone to shop.</li>
                </ol>
              </div>

              <div className="grid sm:grid-cols-3 gap-4">
                <div data-testid="influencer-earnings" className="bg-white border border-[#E8956B]/40 rounded-2xl p-5">
                  <Wallet className="text-[#E8956B] mb-1" size={20} />
                  <div className="text-xs text-neutral-500 uppercase tracking-wide">Commission Earned</div>
                  <div className="text-2xl font-black">${(user.influencer_earnings || 0).toFixed(2)}</div>
                  {user.influencer_pending_earnings > 0 && (
                    <div data-testid="influencer-pending" className="text-[11px] text-amber-600 font-semibold mt-1">
                      +${user.influencer_pending_earnings.toFixed(2)} pending — set up payouts to release
                    </div>
                  )}
                </div>
                <div className="bg-white border border-neutral-200 rounded-2xl p-5">
                  <Sparkles className="text-[#E8956B] mb-1" size={20} />
                  <div className="text-xs text-neutral-500 uppercase tracking-wide">Your Referral Code</div>
                  <div className="text-lg font-bold tracking-wider" data-testid="influencer-code">{user.influencer_code || "—"}</div>
                </div>
                <div className="bg-white border border-neutral-200 rounded-2xl p-5">
                  <Link2 className="text-[#E8956B] mb-1" size={20} />
                  <div className="text-xs text-neutral-500 uppercase tracking-wide">Payouts</div>
                  {connectStatus?.onboarded ? (
                    <div className="text-sm font-semibold text-green-600 mt-1 flex items-center gap-1"><CheckCircle2 size={15} /> Ready</div>
                  ) : (
                    <button data-testid="influencer-connect-btn" onClick={connectPayout} className="mt-1 text-sm font-bold text-[#E8956B] hover:underline">
                      Set up payouts →
                    </button>
                  )}
                </div>
              </div>

              <div data-testid="creator-page-card" className="bg-gradient-to-br from-neutral-900 to-neutral-800 text-white rounded-2xl p-5">
                <div className="flex items-center gap-2 text-[#E8956B] font-bold uppercase tracking-[0.2em] text-xs mb-2">
                  <Crown size={16} /> Your Creator Page
                </div>
                <p className="text-sm text-neutral-300">
                  Your own page showing the products you've picked, plus a VIP sign-up. Earn commission on every pick sold
                  and <b className="text-white">30% ($1.50/mo)</b> for each VIP member who joins through your page.
                </p>
                <div className="mt-4 flex items-center gap-2 bg-white/10 rounded-xl p-2 pl-4">
                  <span data-testid="creator-page-link" className="flex-1 min-w-0 truncate text-sm text-neutral-200">{creatorLink}</span>
                  <button data-testid="copy-creator-link-btn" onClick={copyCreatorLink}
                    className="flex items-center gap-1 text-sm bg-[#E8956B] hover:bg-[#CE744A] text-white font-bold rounded-full px-4 py-2 transition-all shrink-0">
                    <Copy size={14} /> Copy
                  </button>
                  <a data-testid="open-creator-page-btn" href={creatorLink} target="_blank" rel="noreferrer"
                    className="flex items-center gap-1 text-sm bg-white/15 hover:bg-white/25 text-white font-bold rounded-full px-4 py-2 transition-all shrink-0">
                    <ExternalLink size={14} /> View
                  </a>
                </div>
                <button data-testid="copy-membership-link-btn" onClick={copyMembershipLink}
                  className="mt-2 text-xs text-neutral-400 hover:text-[#E8956B] underline transition-colors">
                  or copy a direct VIP membership link
                </button>
                <div className="mt-4 pt-4 border-t border-white/10">
                  <div className="text-xs text-neutral-400 mb-2">Share your page in one tap — perfect for your Instagram or TikTok bio:</div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button data-testid="share-page-btn" onClick={shareCreatorPage}
                      className="flex items-center gap-1.5 bg-[#E8956B] hover:bg-[#CE744A] text-white font-bold rounded-full px-4 py-2 text-sm transition-all">
                      <Share2 size={15} /> Share my page
                    </button>
                    <button data-testid="share-whatsapp-btn" onClick={() => shareTo("whatsapp")} aria-label="Share on WhatsApp"
                      className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all"><MessageCircle size={16} /></button>
                    <button data-testid="share-x-btn" onClick={() => shareTo("x")} aria-label="Share on X"
                      className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all"><Twitter size={16} /></button>
                    <button data-testid="share-facebook-btn" onClick={() => shareTo("facebook")} aria-label="Share on Facebook"
                      className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all"><Facebook size={16} /></button>
                  </div>
                </div>
              </div>

              <div data-testid="earnings-estimator" className="bg-white border border-[#E8956B]/40 rounded-2xl p-5">
                <h3 className="font-display text-lg font-bold flex items-center gap-2"><TrendingUp size={18} className="text-[#E8956B]" /> Earnings Estimator</h3>
                <p className="text-sm text-neutral-500 mt-1">See what you could earn as your page grows. You keep <b>10%</b> of every sale and <b>$1.50/mo</b> per VIP member you refer.</p>
                <div className="mt-5 grid sm:grid-cols-2 gap-6">
                  <div>
                    <div className="flex justify-between text-sm font-semibold mb-1">
                      <span>Monthly sales through your page</span>
                      <span className="text-[#E8956B]" data-testid="est-sales-value">${estSales.toLocaleString()}</span>
                    </div>
                    <input data-testid="est-sales-slider" type="range" min="0" max="5000" step="50"
                      value={estSales} onChange={(e) => setEstSales(Number(e.target.value))}
                      className="w-full accent-[#E8956B]" />
                  </div>
                  <div>
                    <div className="flex justify-between text-sm font-semibold mb-1">
                      <span>VIP members you refer</span>
                      <span className="text-[#E8956B]" data-testid="est-vips-value">{estVips}</span>
                    </div>
                    <input data-testid="est-vips-slider" type="range" min="0" max="200" step="1"
                      value={estVips} onChange={(e) => setEstVips(Number(e.target.value))}
                      className="w-full accent-[#E8956B]" />
                  </div>
                </div>
                <div className="mt-5 flex items-baseline gap-2 bg-[#E8956B]/10 rounded-xl p-4">
                  <span className="text-sm text-neutral-600">You could earn about</span>
                  <span data-testid="est-earnings" className="font-display text-3xl font-black text-neutral-900">${estEarnings.toFixed(2)}</span>
                  <span className="text-sm text-neutral-600">/month</span>
                </div>
                <p className="text-[11px] text-neutral-400 mt-2">Estimate only — actual earnings depend on real sales and active VIP members.</p>
              </div>

              <div className="bg-white border border-neutral-200 rounded-2xl p-5">
                <h3 className="font-display text-lg font-bold mb-3 flex items-center gap-2"><Plus size={18} className="text-[#E8956B]" /> Add a Pick</h3>
                <div className="space-y-3">
                  <select
                    data-testid="pick-product-select"
                    value={pickProductId}
                    onChange={(e) => setPickProductId(e.target.value)}
                    className="w-full bg-neutral-50 border border-neutral-300 rounded-lg h-11 px-3 text-sm outline-none focus:border-[#E8956B]"
                  >
                    <option value="">Select a product to feature…</option>
                    {allProducts.map((p) => (
                      <option key={p.id} value={p.id}>{p.title} — ${p.price.toFixed(2)}</option>
                    ))}
                  </select>
                  <Textarea
                    data-testid="pick-note-input"
                    placeholder="Why do you love it? (shown to shoppers)"
                    value={pickNote}
                    onChange={(e) => setPickNote(e.target.value)}
                    className="bg-neutral-50 border-neutral-300"
                  />
                  <button data-testid="create-pick-btn" onClick={createPick} className="bg-[#E8956B] hover:bg-[#CE744A] text-white font-bold rounded-full px-6 py-2 text-sm transition-all">
                    Publish Pick
                  </button>
                </div>
              </div>

              <div>
                <h3 className="font-display text-lg font-bold mb-3">My Picks ({myPicks.length})</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  {myPicks.map((pick) => (
                    <div key={pick.id} data-testid={`my-pick-${pick.id}`} className="flex items-center gap-3 bg-white border border-neutral-200 rounded-xl p-3">
                      <img src={imgUrl(pick.product.images?.[0])} alt="" className="w-14 h-14 rounded-lg object-cover" />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm truncate">{pick.product.title}</div>
                        {pick.note && <div className="text-xs text-neutral-500 italic truncate">"{pick.note}"</div>}
                      </div>
                      <button data-testid={`copy-pick-${pick.id}`} onClick={() => copyPickLink(pick)} className="text-neutral-400 hover:text-[#E8956B]" title="Copy referral link"><Copy size={16} /></button>
                      <button data-testid={`del-pick-${pick.id}`} onClick={() => delPick(pick.id)} className="text-neutral-400 hover:text-red-400"><Trash2 size={16} /></button>
                    </div>
                  ))}
                  {myPicks.length === 0 && <p className="text-neutral-400 text-sm">No picks yet — add your first above.</p>}
                </div>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!rateOrder} onOpenChange={(o) => !o && setRateOrder(null)}>
        <DialogContent className="bg-white border-neutral-200 text-neutral-900 sm:max-w-md" data-testid="rate-seller-modal">
          <DialogHeader><DialogTitle>Rate your seller</DialogTitle></DialogHeader>
          <p className="text-sm text-neutral-500 -mt-2">How was your experience with <b>{rateOrder?.seller_name}</b>?</p>
          <div className="flex gap-1 my-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} data-testid={`rate-star-${n}`} onClick={() => setRateStars(n)} className="transition-transform hover:scale-110">
                <Star size={30} className={n <= rateStars ? "text-[#E8956B]" : "text-neutral-300"} fill={n <= rateStars ? "currentColor" : "none"} />
              </button>
            ))}
          </div>
          <Textarea data-testid="rate-comment" value={rateComment} onChange={(e) => setRateComment(e.target.value)} placeholder="Share details about your experience (optional)" className="bg-neutral-50 border-neutral-300" />
          <button data-testid="submit-seller-review" onClick={submitSellerReview} className="mt-2 w-full bg-[#E8956B] hover:bg-[#CE744A] text-white font-bold rounded-full py-2.5 transition-all">
            Submit Review
          </button>
        </DialogContent>
      </Dialog>


      <Dialog open={!!shipOrder} onOpenChange={(o) => !o && setShipOrder(null)}>
        <DialogContent className="bg-white border-neutral-200 text-neutral-900 sm:max-w-md" data-testid="ship-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Mark as Shipped</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-neutral-500 -mt-2">Let the buyer know how you sent <span className="font-semibold text-neutral-900">{shipOrder?.product_title}</span>.</p>
          <div className="flex items-center gap-2 rounded-xl bg-[#E8956B]/10 border border-[#E8956B]/30 p-2.5 text-[12px] text-neutral-700">
            <Heart size={15} className="text-[#E8956B] shrink-0" fill="currentColor" />
            <span>Don't forget to sanitize the item and pop your <strong>"Packed with love"</strong> sticker &amp; thank-you card in the box! (Download them in your Sales tab.)</span>
          </div>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#E8956B]">How did you ship it?</label>
              <Input data-testid="ship-carrier-input" placeholder="e.g. USPS Priority, UPS, FedEx, hand delivery" value={carrier} onChange={(e) => setCarrier(e.target.value)} className="bg-neutral-50 border-neutral-300 h-11 mt-2" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#E8956B]">Tracking number (optional)</label>
              <Input data-testid="ship-tracking-input" placeholder="Tracking / reference number" value={tracking} onChange={(e) => setTracking(e.target.value)} className="bg-neutral-50 border-neutral-300 h-11 mt-2" />
            </div>
            <button data-testid="ship-submit-btn" onClick={submitShip} className="w-full bg-[#E8956B] hover:bg-[#CE744A] text-white font-bold rounded-full py-3 transition-all">
              Notify Buyer &amp; Mark Shipped
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!swapShip} onOpenChange={(o) => !o && setSwapShip(null)}>
        <DialogContent className="bg-white border-neutral-200 text-neutral-900 sm:max-w-md" data-testid="swap-ship-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Ship your swap item</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-neutral-500 -mt-2">Let your trade partner know it's on the way. Once both items are confirmed received, the swap completes.</p>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#E8956B]">How did you ship it?</label>
              <Input data-testid="swap-ship-carrier-input" placeholder="e.g. USPS Priority, UPS, FedEx" value={swapCarrier} onChange={(e) => setSwapCarrier(e.target.value)} className="bg-neutral-50 border-neutral-300 h-11 mt-2" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#E8956B]">Tracking number (optional)</label>
              <Input data-testid="swap-ship-tracking-input" placeholder="Tracking / reference number" value={swapTracking} onChange={(e) => setSwapTracking(e.target.value)} className="bg-neutral-50 border-neutral-300 h-11 mt-2" />
            </div>
            <button data-testid="swap-ship-submit-btn" disabled={swapBusy} onClick={submitSwapShip} className="w-full bg-[#E8956B] hover:bg-[#CE744A] text-white font-bold rounded-full py-3 transition-all disabled:opacity-50">
              Mark Shipped
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rateSwap} onOpenChange={(o) => !o && setRateSwap(null)}>
        <DialogContent className="bg-white border-neutral-200 text-neutral-900 sm:max-w-md" data-testid="swap-review-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Rate your trade</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-neutral-500 -mt-2">How was your swap with <span className="font-semibold text-neutral-900">{rateSwap?.otherName}</span>? Your rating helps the community trade with confidence.</p>
          <div className="space-y-4 mt-2">
            <StarRating value={rateSwapStars} size={28} editable onChange={setRateSwapStars} />
            <Textarea data-testid="swap-review-comment" placeholder="Share how the trade went (optional)" value={rateSwapComment} onChange={(e) => setRateSwapComment(e.target.value)} className="bg-neutral-50 border-neutral-300 min-h-24" />
            <button data-testid="swap-review-submit-btn" disabled={swapBusy} onClick={submitSwapReview} className="w-full bg-[#E8956B] hover:bg-[#CE744A] text-white font-bold rounded-full py-3 transition-all disabled:opacity-50">
              Submit rating
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!issueOrder} onOpenChange={(o) => !o && setIssueOrder(null)}>
        <DialogContent className="bg-white border-neutral-200 text-neutral-900 sm:max-w-md" data-testid="issue-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Report a Problem</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-neutral-500 -mt-2">Tell us what went wrong with <span className="font-semibold text-neutral-900">{issueOrder?.product_title}</span>. Our team will review and hold the seller's payout until it's resolved.</p>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#E8956B]">What happened?</label>
              <Textarea data-testid="issue-reason-input" placeholder="e.g. The product arrived damaged / doesn't match the listing / never arrived..." value={issueReason} onChange={(e) => setIssueReason(e.target.value)} className="bg-neutral-50 border-neutral-300 min-h-28 mt-2" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#E8956B]">Photos of the product</label>
              <div className="grid grid-cols-4 gap-3 mt-2">
                {issueImages.map((img, i) => (
                  <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-neutral-200">
                    <img src={imgUrl(img)} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => setIssueImages(issueImages.filter((_, x) => x !== i))} className="absolute top-1 right-1 bg-black/70 rounded-full p-1 text-white hover:text-[#E8956B]">
                      <X size={12} />
                    </button>
                  </div>
                ))}
                <label data-testid="issue-upload-label" className="aspect-square rounded-xl border-2 border-dashed border-neutral-300 flex flex-col items-center justify-center cursor-pointer hover:border-[#E8956B] transition-colors text-neutral-400">
                  {issueUploading ? <Loader2 size={18} className="animate-spin" /> : <><Upload size={18} /><span className="text-[10px] mt-1">Add</span></>}
                  <input data-testid="issue-upload-input" type="file" accept="image/*" multiple className="hidden" onChange={uploadIssueImg} />
                </label>
              </div>
            </div>
            <button data-testid="issue-submit-btn" onClick={submitIssue} className="w-full bg-red-600 hover:bg-red-700 text-white font-bold rounded-full py-3 transition-all">
              Alert the Platform
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
